/**
 * Phase 41 Plan 41-05 Task 3 — KpiStrip.
 *
 * Renders the four-tile KPI strip on the Dashboard with values wired to
 * real data via `useDashboardKpis`. Per checker I-07, the strip ships
 * with REAL numbers — the "—" placeholder ONLY appears while a source hook
 * is still loading (value is null), never as a hard-coded TODO.
 *
 * F-02 (2026-05-13 UAT batch Phase 3) — three tiles now show LAST
 * CALENDAR MONTH (Hours / Expenses / Invoiced); Time Off stays YTD.
 *
 * F-03 (2026-05-13) — Invoiced tile sub-label surfaces a sent /
 * not-sent split (invoiced vs uninvoiced approved billable amount)
 * when the user has access to /reports/invoicing-status; falls back
 * to "issued last month" for everyone else.
 */
import { KpiCard } from './KpiCard';
import { useDashboardKpis } from '../../hooks/useDashboardKpis';
import { LEGACY_EN_NUMBER_LOCALE, numberFormatter } from '../../utils/localeFormat';

function fmtCurrency(n: number, ccy: string): string {
  return numberFormatter({
    style: 'currency',
    currency: ccy,
    maximumFractionDigits: 0,
  }, LEGACY_EN_NUMBER_LOCALE).format(n);
}

function fmtHours(n: number): string {
  return `${n.toFixed(1)}h`;
}

function fmtDays(n: number): string {
  return `${n}`;
}

export function KpiStrip() {
  const kpis = useDashboardKpis();

  const hoursValue = kpis.hoursThisMonth === null ? '—' : fmtHours(kpis.hoursThisMonth);
  const expensesValue =
    kpis.approvedExpensesThisMonth === null
      ? '—'
      : fmtCurrency(kpis.approvedExpensesThisMonth, kpis.expensesCurrency);
  const invoicedValue =
    kpis.invoicedThisMonth === null
      ? '—'
      : fmtCurrency(kpis.invoicedThisMonth, kpis.invoicedCurrency);
  const timeOffValue =
    kpis.timeOffRemainingDays === null ? '—' : fmtDays(kpis.timeOffRemainingDays);

  // F-03 — invoiced split sub-label. When the split is available we
  // swap the generic "last month" sub-label for the actionable
  // "sent / not-sent" breakdown so a user can see at a glance how
  // much approved billing is still un-invoiced.
  const invoicedSubLabel = kpis.invoicedSplit
    ? `sent ${fmtCurrency(kpis.invoicedSplit.invoicedAmount, kpis.invoicedCurrency)} · not sent ${fmtCurrency(kpis.invoicedSplit.uninvoicedAmount, kpis.invoicedCurrency)}`
    : 'issued last month';

  /**
   * MKS-332 — this tile sums invoices by the month they were ISSUED
   * (`Invoice.createdAt`), which is NOT the Invoiced metric the reports use
   * (that one attributes by the invoice's SERVICE period). The ticket allows
   * either switching the computation or labelling it explicitly; labelling is
   * what ships here, so the tile stops looking like a smaller, disagreeing
   * copy of the Monthly Revenue figure and says which question it answers.
   */
  const invoicedLabel = 'Invoiced (issued)';
  const invoicedTooltip =
    'Invoices ISSUED last month, by issue date. Not the same as Invoiced on the reports, which attributes an invoice to the period it served.';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <KpiCard label="Hours" value={hoursValue} subLabel="last month" />
      <KpiCard label="Approved Expenses" value={expensesValue} subLabel="last month" />
      <KpiCard
        label={invoicedLabel}
        value={invoicedValue}
        subLabel={invoicedSubLabel}
        tooltip={invoicedTooltip}
      />
      <KpiCard label="Time Off" value={timeOffValue} subLabel="days remaining" />
    </div>
  );
}
